/** Change status oynasi (eskiz 9, o'ng ustun): job, start date, sabab -- backend qoidalariga mos. */
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { useMe } from "@/features/auth/hooks";
import { isManager } from "@/features/auth/types";
import { driverKeys, useAppSettings, useChangeStatus, useOpenJobs } from "@/features/drivers/api";
import { LOST_STATUSES, PIPELINE_ORDER, type DriverListItem, type DriverStatus } from "@/features/drivers/types";
import { kindLabel } from "@/features/documents/status";
import type { DocumentsSummary, DocumentType } from "@/features/documents/types";
import { allowedTargets, NEEDS_JOB } from "@/features/drivers/transitions";
import { Button } from "@/components/ui/button";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";

export interface ChangeStatusModalProps {
  driver: Pick<DriverListItem, "id" | "status" | "version" | "current_job" | "full_name"> & { start_date?: string | null };
  /** Profil beradi: "submitted" tanlanganda yetishmayotgan hujjatlar ogohlantirishi (13-hujjatlar.md) */
  documentsSummary?: DocumentsSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialStatus?: DriverStatus;
}

export function ChangeStatusModal({ driver, documentsSummary, open, onOpenChange, initialStatus }: ChangeStatusModalProps) {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const { t: td } = useTranslation("documents");
  const me = useMe().data;
  const manager = me ? isManager(me.role) : false;
  const qc = useQueryClient();
  const settings = useAppSettings();
  const targets = useMemo(() => allowedTargets(driver.status, manager), [driver.status, manager]);
  const [status, setStatus] = useState<DriverStatus | "">("");
  const [job, setJob] = useState<string>("");
  const [startDate, setStartDate] = useState("");
  const [reason, setReason] = useState("");
  const appSettings = useAppSettings();
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const change = useChangeStatus(driver.id);
  const needsJob = status !== "" && NEEDS_JOB.includes(status);
  const jobs = useOpenJobs(open && needsJob);

  useEffect(() => {
    if (!open) return;
    const preset = initialStatus && targets.includes(initialStatus) ? initialStatus : "";
    setStatus(preset);
    setJob(driver.current_job ? String(driver.current_job.id) : "");
    setStartDate(driver.start_date ?? "");
    setReason("");
    setNote("");
    setErrors({});
  }, [open, initialStatus, targets, driver.current_job, driver.start_date]);

  const jobOptions = useMemo(() => {
    const list = (jobs.data ?? []).map((j) => ({ id: j.id, label: `${j.title} · ${j.carrier_name}` }));
    const cur = driver.current_job;
    if (cur && !list.some((j) => j.id === cur.id)) list.unshift({ id: cur.id, label: `${cur.title} · ${cur.carrier_name}${cur.status !== "open" ? ` (${cur.status})` : ""}` });
    return list;
  }, [jobs.data, driver.current_job]);

  const lostReasonRequired = appSettings.data?.lost_reason_required ?? true; // sozlama: yo'qotish sababi majburiymi
  const reasons: string[] = useMemo(() => {
    const s = settings.data;
    if (!s) return [];
    if (status === "rejected") return s.rejection_reasons ?? [];
    if (status === "not_interested") return s.lost_reasons ?? [];
    if (status === "quit") return s.quit_reasons ?? [];
    return [];
  }, [settings.data, status]);

  const isLost = status !== "" && LOST_STATUSES.includes(status);
  // Hujjatlar yetishmasa (so'ralgani ham): ogohlantirish; sozlamada to'siq yoqiq bo'lsa backend 400 qaytaradi
  const missingDocs = useMemo(() => {
    if (status !== "submitted" || !documentsSummary) return [];
    const types = (settings.data?.document_types ?? []) as DocumentType[];
    return [...documentsSummary.missing, ...documentsSummary.requested].map((k) => kindLabel(td, k, types.find((x) => x.key === k)?.label ?? k));
  }, [status, documentsSummary, settings.data, td]);

  async function submit() {
    if (!status) {
      setErrors({ status: t("statusModal.errors.chooseStatus") });
      return;
    }
    const next: Record<string, string> = {};
    if (needsJob && !job) next.job = status === "hired" ? t("statusModal.errors.jobRequired") : t("statusModal.errors.chooseJob");
    if (status === "hired" && !startDate) next.start_date = t("statusModal.errors.startDate");
    if (isLost && !reason && lostReasonRequired) next.reason = t("statusModal.errors.reason");
    setErrors(next);
    if (Object.keys(next).length) return;
    try {
      await change.mutateAsync({
        status,
        reason: isLost ? reason : undefined,
        note: note || undefined,
        job: needsJob ? Number(job) : undefined,
        start_date: status === "hired" ? startDate : undefined,
        version: driver.version,
      });
      toast.success(t("statusModal.updated"), `${driver.full_name} → ${tc(`status.${status}`)}`);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) {
        toast.warning(t("statusModal.changedBy", { who: err.changedBy ?? t("statusModal.someoneElse") }), t("statusModal.reloadedHint"));
        qc.invalidateQueries({ queryKey: driverKeys.detail(driver.id) });
        qc.invalidateQueries({ queryKey: driverKeys.all });
        onOpenChange(false);
        return;
      }
      if (err instanceof ApiError && err.fields) {
        setErrors(Object.fromEntries(Object.entries(err.fields).map(([k, v]) => [k, v.join(" ")])));
        return;
      }
      toast.error(err instanceof ApiError ? err.message : t("statusModal.failed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("statusModal.title")}
      description={t("statusModal.now", { name: driver.full_name, status: tc(`status.${driver.status}`) })}
      size="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={change.isPending} onClick={submit} disabled={!status}>
            {tc("actions.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t("statusModal.newStatus")} required error={errors.status} htmlFor="cs-status">
          <Select id="cs-status" value={status} onChange={(e) => setStatus(e.target.value as DriverStatus | "")} aria-invalid={errors.status ? true : undefined} autoFocus>
            <option value="">{t("statusModal.select")}</option>
            <optgroup label={t("statusModal.pipeline")}>
              {PIPELINE_ORDER.map((s) => (
                <option key={s} value={s} disabled={!targets.includes(s)}>
                  {tc(`status.${s}`)}
                  {s === driver.status ? ` ${t("statusModal.current")}` : ""}
                </option>
              ))}
            </optgroup>
            <optgroup label={t("statusModal.lost")}>
              {LOST_STATUSES.map((s) => (
                <option key={s} value={s} disabled={!targets.includes(s)}>
                  {tc(`status.${s}`)}
                  {s === driver.status ? ` ${t("statusModal.current")}` : ""}
                </option>
              ))}
            </optgroup>
          </Select>
        </Field>

        {missingDocs.length > 0 && (
          <p className="rounded-xl bg-warning-soft px-3 py-2 text-[12.5px] text-warning">
            {t("statusModal.docsMissing", { names: missingDocs.join(", ") })}
            {settings.data?.documents_block_submit ? ` ${t("statusModal.docsBlocked")}` : ""}
          </p>
        )}

        {needsJob && (
          <Field label={t("statusModal.job")} required error={errors.job} htmlFor="cs-job" hint={t("statusModal.jobHint")}>
            <Select id="cs-job" value={job} onChange={(e) => setJob(e.target.value)} aria-invalid={errors.job ? true : undefined}>
              <option value="">{jobs.isPending ? t("statusModal.loading") : t("statusModal.select")}</option>
              {jobOptions.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.label}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {status === "hired" && (
          <Field label={t("statusModal.startDate")} required error={errors.start_date} htmlFor="cs-start">
            <Input id="cs-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} aria-invalid={errors.start_date ? true : undefined} className="w-44" />
          </Field>
        )}

        {isLost && (
          <Field label={t("statusModal.reason")} required={lostReasonRequired} error={errors.reason} htmlFor="cs-reason">
            <Select id="cs-reason" value={reason} onChange={(e) => setReason(e.target.value)} aria-invalid={errors.reason ? true : undefined}>
              <option value="">{t("statusModal.select")}</option>
              {reasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label={t("statusModal.note")} htmlFor="cs-note">
          <Textarea id="cs-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={status === "hired" ? t("statusModal.notePlaceholderHired") : t("statusModal.optional")} />
        </Field>

        {status === "hired" && <p className="text-[11px] text-muted">{t("statusModal.hiredNote")}</p>}
        {status === "submitted" && <p className="text-[11px] text-muted">{t("statusModal.submittedNote")}</p>}
      </div>
    </Modal>
  );
}
